import {
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@prelude-ai/design-system"
import { useQuery } from "@tanstack/react-query"
import { Link, useNavigate } from "@tanstack/react-router"
import { ArrowLeft, History, Loader2, Play } from "lucide-react"
import { useCallback, useEffect, useState } from "react"
import type { OutwardConversation } from "../../utils/conversation-store.ts"
import { API_BASE_URL } from "../config"
import { toast } from "../utils/toast.tsx"

type ConversationSelectorProps = {
  agent: string
  activeConversationId?: string
  isNavigating?: boolean
}

async function fetchConversations(agentName: string): Promise<OutwardConversation[]> {
  const res = await fetch(`${API_BASE_URL}/api/conversations?agentName=${encodeURIComponent(agentName)}`, {
    credentials: "include"
  })

  if (!res.ok) {
    throw new Error(`GET /api/conversations returned ${res.status} for agent "${agentName}"`)
  }

  const data = await res.json()
  return data.items
}

function ConversationListView({
  conversations,
  activeConversationId,
  isNavigating,
  onSelectConversation
}: {
  conversations: OutwardConversation[]
  activeConversationId?: string
  isNavigating?: boolean
  onSelectConversation: (conversation: OutwardConversation) => void
}) {
  return (
    <div className="relative">
      {isNavigating && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/80">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}
      <div className="max-h-96 overflow-auto">
        <table className="w-full caption-bottom text-sm">
          <thead className="[&_tr]:border-b">
            <tr className="border-b">
              <th className="w-12 h-10 px-2 text-left align-middle font-medium sticky top-0 z-10 bg-background" />
              <th className="h-10 px-2 text-left align-middle font-medium sticky top-0 z-10 bg-background">
                Last Message
              </th>
              <th className="w-24 h-10 px-2 text-right align-middle font-medium sticky top-0 z-10 bg-background">
                Messages
              </th>
              <th className="w-28 h-10 px-2 text-right align-middle font-medium sticky top-0 z-10 bg-background">
                Created
              </th>
              <th className="w-28 h-10 px-2 text-right align-middle font-medium sticky top-0 z-10 bg-background">
                Updated
              </th>
            </tr>
          </thead>
          <tbody className="[&_tr:last-child]:border-0">
            {conversations.map((conversation) => {
              const lastItem = conversation.conversationState?.transcript.length - 1
              const rawMessage = conversation.conversationState?.transcript?.[lastItem]?.response || "Empty Message"
              const lastMessage = rawMessage.length > 80 ? `${rawMessage.slice(0, 80)}...` : rawMessage
              const isActive = conversation.id === activeConversationId
              return (
                <tr
                  key={conversation.id}
                  className="border-b transition-colors hover:bg-muted/50 cursor-pointer"
                  onClick={() => onSelectConversation(conversation)}
                >
                  <td className="p-2 align-middle">
                    {isActive && (
                      <Chip className="bg-green-300 dark:bg-green-800 dark:text-green-50 text-green-900 border-transparent">
                        Active
                      </Chip>
                    )}
                  </td>
                  <td className="p-2 align-middle">{lastMessage}</td>
                  <td className="p-2 align-middle text-right text-muted-foreground">
                    {conversation.conversationState?.transcript?.length || 0}
                  </td>
                  <td className="p-2 align-middle text-right text-muted-foreground">
                    {conversation.createdAt ? new Date(conversation.createdAt).toLocaleDateString() : "-"}
                  </td>
                  <td className="p-2 align-middle text-right text-muted-foreground">
                    {conversation.updatedAt ? new Date(conversation.updatedAt).toLocaleDateString() : "-"}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function ConversationDetailView({
  conversation,
  agent,
  onBack,
  onClose
}: {
  conversation: OutwardConversation
  agent: string
  onBack: () => void
  onClose: () => void
}) {
  const [isForking, setIsForking] = useState(false)
  const navigate = useNavigate()

  const displayMessages = conversation.conversationState?.transcript
    .map((entry, index) => ({ ...entry, originalIndex: index }))
    .filter((entry) => (entry.type === "user_input" || entry.type === "assistant") && entry.response?.trim())

  const handleFork = useCallback(
    async (originalIndex: number) => {
      if (isForking) return
      setIsForking(true)

      const slicedTranscript = conversation.conversationState.transcript.slice(0, originalIndex + 1)

      try {
        const res = await fetch(`${API_BASE_URL}/api/conversations`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ agentName: agent, transcript: slicedTranscript })
        })

        if (!res.ok) {
          toast.error("Failed to fork conversation", {
            description: `POST /api/conversations returned ${res.status} for agent "${agent}" (forking at index ${originalIndex})`
          })
          return
        }

        const data = await res.json()
        const conversationId = data?.conversationId
        if (conversationId) {
          onClose()
          navigate({
            to: "/conversations/$conversationId",
            params: { conversationId },
            search: (prev) => ({ ...prev })
          })
        }
      } catch (error) {
        toast.error("Failed to fork conversation", {
          description: `POST /api/conversations threw for agent "${agent}" (forking at index ${originalIndex}): ${error instanceof Error ? error.message : "Network error"}`
        })
      } finally {
        setIsForking(false)
      }
    },
    [agent, conversation.conversationState.transcript, isForking, navigate, onClose]
  )

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" className="gap-1" onClick={onBack} disabled={isForking}>
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        <Button variant="outline" size="sm" className="gap-1" asChild>
          <Link
            to="/conversations/view/$conversationId"
            params={{ conversationId: conversation.id }}
            search={(prev) => ({ ...prev, agent })}
            onClick={onClose}
          >
            Load Full Conversation
          </Link>
        </Button>
      </div>

      {isForking && (
        <div className="flex items-center justify-center py-4 gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Creating conversation...
        </div>
      )}

      <div className="max-h-96 overflow-auto space-y-1">
        {displayMessages?.map((entry, displayIndex) => {
          const isUser = entry.type === "user_input"
          const content = entry.response.length > 100 ? `${entry.response.slice(0, 100)}...` : entry.response
          return (
            <div
              key={entry.originalIndex}
              className="flex items-start gap-2 rounded-md border px-3 py-2 text-sm hover:bg-muted/50"
            >
              <span className="shrink-0 text-xs text-muted-foreground pt-0.5 w-10">#{displayIndex + 1}</span>
              <Chip
                className={
                  isUser
                    ? "shrink-0 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 border-transparent"
                    : "shrink-0 bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 border-transparent"
                }
              >
                {isUser ? "User" : "Assistant"}
              </Chip>
              <span className="min-w-0 flex-1 text-muted-foreground">{content}</span>
              <Button
                variant="ghost"
                size="sm"
                className="shrink-0 gap-1 text-xs"
                onClick={() => handleFork(entry.originalIndex)}
                disabled={isForking}
              >
                <Play className="h-3 w-3" />
                Continue from here
              </Button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export const ConversationSelector = ({ agent, activeConversationId, isNavigating }: ConversationSelectorProps) => {
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedConversation, setSelectedConversation] = useState<OutwardConversation | null>(null)
  const {
    data: conversations,
    isLoading,
    error
  } = useQuery({
    queryKey: ["conversations", agent],
    queryFn: () => fetchConversations(agent),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: modalOpen
  })

  const handleOpenModal = useCallback((isOpen: boolean) => {
    setModalOpen(isOpen)
    if (!isOpen) {
      setSelectedConversation(null)
    }
  }, [])

  useEffect(() => {
    if (isNavigating) {
      setModalOpen(false)
    }
  }, [isNavigating])

  return (
    <>
      <Button variant="outline" size="sm" className="gap-1" onClick={() => setModalOpen(true)}>
        <History className="h-4 w-4" />
        <span className="hidden sm:inline">Load Previous Conversations</span>
      </Button>
      <Dialog modal onOpenChange={handleOpenModal} open={modalOpen}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{selectedConversation ? "Conversation Messages" : "Load Conversation"}</DialogTitle>
            <DialogDescription>
              {selectedConversation
                ? "Select a message to continue the conversation from that point"
                : "Resume a previous conversation"}
            </DialogDescription>
          </DialogHeader>
          {isLoading && (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}
          {error && <p className="text-sm text-destructive py-4">Failed to load conversations.</p>}
          {conversations && conversations.length === 0 && !selectedConversation && (
            <p className="text-sm text-muted-foreground py-4">No previous conversations found.</p>
          )}
          {conversations && conversations.length > 0 && !selectedConversation && (
            <ConversationListView
              conversations={conversations}
              activeConversationId={activeConversationId}
              isNavigating={isNavigating}
              onSelectConversation={setSelectedConversation}
            />
          )}
          {selectedConversation && (
            <ConversationDetailView
              conversation={selectedConversation}
              agent={agent}
              onBack={() => setSelectedConversation(null)}
              onClose={() => setModalOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
